/**
 * CredentialPermissionsEditor — thin adapter around the shared PermissionEditor.
 *
 * Wires REST API endpoints for credential role management into the modular
 * permission editor. Supports user + group role assignments on credentials.
 *
 * API: /api/workspace/:id/credentials/:credId/roles (GET, POST, DELETE)
 */
import {
  defaultWorkspaceApiClient,
  WorkspaceApiError,
  WorkspaceServerService,
} from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { getGroupsQuery, getMembersByWorkspaceIdQuery } from '@affine/graphql';
import { useService } from '@toeverything/infra';
import { useMemo } from 'react';

import { PermissionEditor } from '../shared/permission-editor';
import type {
  PermissionEditorAdapter,
  PermissionEditorConfig,
  PermissionEntry,
} from '../shared/permission-editor-types';

// =============================================================================
// Config — Credentials have no ownership concept (flat hierarchy)
// =============================================================================

const CREDENTIAL_PERMISSION_CONFIG: PermissionEditorConfig = {
  allRoles: ['Owner', 'Admin', 'Editor', 'Reader'],
  grantableRoles: ['Admin', 'Editor', 'Reader'],
  ownerRole: 'Owner',
  demoteOnTransferRole: 'Admin',
  defaultGrantRole: 'Editor',
  testIdPrefix: 'credential-permissions',
};

function credentialRoleRequestKey(
  credentialId: string,
  action: string,
  target?: string
) {
  return `credential-roles:${credentialId}:${action}${target ? `:${target}` : ''}`;
}

// =============================================================================
// REST API Response Type
// =============================================================================

interface CredentialRoleResponse {
  id: string;
  type: 'user' | 'group';
  targetId: string;
  targetName: string;
  targetEmail?: string;
  targetAvatarUrl?: string | null;
  role: string;
}

// =============================================================================
// Component
// =============================================================================

interface CredentialPermissionsEditorProps {
  credentialId: string;
}

export const CredentialPermissionsEditor = ({
  credentialId,
}: CredentialPermissionsEditorProps) => {
  const workspaceService = useService(WorkspaceService);
  const serverService = useService(WorkspaceServerService);
  const workspaceId = workspaceService.workspace.id;

  const adapter = useMemo(
    (): PermissionEditorAdapter => ({
      async loadEntries(): Promise<PermissionEntry[]> {
        const json = await defaultWorkspaceApiClient
          .requestJson<{ roles?: CredentialRoleResponse[] }>({
            workspaceId,
            path: `/api/workspace/${workspaceId}/credentials/${credentialId}/roles`,
            category: 'settings',
            key: credentialRoleRequestKey(credentialId, 'list'),
            mode: 'background',
            priority: 'visible',
          })
          .catch(error => {
            if (error instanceof WorkspaceApiError && error.status === 403) {
              return { roles: [] };
            }
            throw error;
          });
        const roles: CredentialRoleResponse[] = json.roles ?? [];

        return roles.map(r => {
          if (r.type === 'user') {
            return {
              type: 'user' as const,
              id: r.id,
              targetId: r.targetId,
              name: r.targetName ?? '',
              email: r.targetEmail ?? '',
              avatarUrl: r.targetAvatarUrl ?? null,
              role: r.role,
            };
          }
          return {
            type: 'group' as const,
            id: r.id,
            targetId: r.targetId,
            name: r.targetName,
            role: r.role,
          };
        });
      },

      async loadMembers(query: string) {
        if (!serverService.server) return [];
        const result = await serverService.server.gql({
          query: getMembersByWorkspaceIdQuery,
          variables: {
            workspaceId,
            take: 50,
            query: query || undefined,
          },
        });
        return (result.workspace?.members ?? []).map(m => ({
          id: m.id,
          name: m.name ?? '',
          email: m.email ?? '',
          avatarUrl: m.avatarUrl ?? null,
        }));
      },

      async loadGroups() {
        if (!serverService.server) return [];
        const result = await serverService.server.gql({
          query: getGroupsQuery,
          variables: { workspaceId },
        });
        return (result.groups ?? []).map(
          (g: { id: string; name: string; description?: string | null }) => ({
            id: g.id,
            name: g.name,
            description: g.description ?? undefined,
          })
        );
      },

      async grantUser(userId: string, role: string) {
        await defaultWorkspaceApiClient.request({
          workspaceId,
          path: `/api/workspace/${workspaceId}/credentials/${credentialId}/roles`,
          init: {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type: 'user', targetId: userId, role }),
          },
          category: 'settings',
          key: credentialRoleRequestKey(credentialId, 'grant-user', userId),
          mode: 'foreground',
          priority: 'foreground',
        });
      },

      async grantGroup(groupId: string, role: string) {
        await defaultWorkspaceApiClient.request({
          workspaceId,
          path: `/api/workspace/${workspaceId}/credentials/${credentialId}/roles`,
          init: {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type: 'group', targetId: groupId, role }),
          },
          category: 'settings',
          key: credentialRoleRequestKey(credentialId, 'grant-group', groupId),
          mode: 'foreground',
          priority: 'foreground',
        });
      },

      async updateRole(entry: PermissionEntry, newRole: string) {
        // REST API uses upsert via POST — same endpoint as grant
        await defaultWorkspaceApiClient.request({
          workspaceId,
          path: `/api/workspace/${workspaceId}/credentials/${credentialId}/roles`,
          init: {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: entry.type,
              targetId: entry.targetId,
              role: newRole,
            }),
          },
          category: 'settings',
          key: credentialRoleRequestKey(credentialId, 'update', entry.targetId),
          mode: 'foreground',
          priority: 'foreground',
        });
      },

      async revoke(entry: PermissionEntry) {
        await defaultWorkspaceApiClient.request({
          workspaceId,
          path: `/api/workspace/${workspaceId}/credentials/${credentialId}/roles/${entry.id}`,
          init: {
            method: 'DELETE',
          },
          category: 'settings',
          key: credentialRoleRequestKey(credentialId, 'revoke', entry.id),
          mode: 'foreground',
          priority: 'foreground',
        });
      },
    }),
    [serverService.server, workspaceId, credentialId]
  );

  return (
    <PermissionEditor config={CREDENTIAL_PERMISSION_CONFIG} adapter={adapter} />
  );
};
